"use client";

import React, { useEffect, useState } from "react";
import { useField, useFormikContext } from "formik";
import DatePickerLib from "react-datepicker";
import { subYears } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import FieldError from "../field-error/FieldError";
import "./DatePicker.css";

type DatePickerMode = "full" | "month-year" | "year";

interface DatePickerProps {
  label?: string;
  name: string;
  placeholder?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  minAge?: number;
  maxAge?: number;
  mode?: DatePickerMode;
  /** optional: force portal on mobile for safe viewport fit */
  usePortalOnMobile?: boolean;
  /** optional: breakpoint in px */
  mobileBreakpoint?: number;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  name,
  placeholder = "Select a date",
  disabled = false,
  minDate,
  maxDate,
  minAge,
  maxAge,
  mode = "full",
  usePortalOnMobile = true,
  mobileBreakpoint = 480,
}) => {
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();
  const [isFocused, setIsFocused] = useState(false);

  const [isMobileLike, setIsMobileLike] = useState(false);

  // Detect mobile / narrow screen → decide when to use portal
  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`);

    const detect = () => {
      const viaWidth = mq.matches;
      const ua = window.navigator.userAgent || "";
      const viaUA = /iPhone|iPad|iPod|Android/i.test(ua);
      setIsMobileLike(viaWidth || viaUA);
    };

    detect();
    mq.addEventListener?.("change", detect);
    return () => mq.removeEventListener?.("change", detect);
  }, [mobileBreakpoint]);

  // Parse stored value into a Date (avoid TZ drift for yyyy-MM-dd)
  let selectedDate: Date | null = null;

  const parseYmdToLocalDate = (ymd: string): Date | null => {
    // Expect "YYYY-MM-DD"
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!m) return null;

    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!y || !mo || !d) return null;

    // Local midnight (no UTC conversion)
    const local = new Date(y, mo - 1, d);
    return isNaN(local.getTime()) ? null : local;
  };

  if (field.value) {
    if (mode === "year") {
      const year = parseInt(field.value);
      selectedDate = isNaN(year) ? null : new Date(year, 0, 1);
    } else if (mode === "month-year") {
      const [y, m] = String(field.value).split("-").map(Number);
      selectedDate = !isNaN(y) && !isNaN(m) ? new Date(y, m - 1, 1) : null;
    } else {
      // full: stored as "YYYY-MM-DD"
      selectedDate = parseYmdToLocalDate(String(field.value));
    }
  }


  // Build min/max based on age and mode
  const today = new Date();
  const currentYear = today.getFullYear();
  const ageMinDate = minAge ? subYears(today, minAge) : undefined; // youngest allowed (max selectable date)
  const ageMaxDate = maxAge ? subYears(today, maxAge) : undefined; // oldest allowed (min selectable date)

  const modeMaxDate =
    mode === "year"
      ? new Date(currentYear, 11, 31)
      : mode === "month-year"
        ? new Date(currentYear, today.getMonth(), 31)
        : undefined;

  const finalMaxDate = maxDate || ageMinDate || modeMaxDate;
  const finalMinDate = minDate || ageMaxDate;

  // Mode configuration
  let dateFormat = "yyyy-MM-dd";
  let showMonthYearPicker = false;
  let showYearPicker = false;

  if (mode === "month-year") {
    dateFormat = "MM-yyyy";
    showMonthYearPicker = true;
  } else if (mode === "year") {
    dateFormat = "yyyy";
    showYearPicker = true;
  }

  // Commit value based on mode
  const handleChange = (date: Date | null) => {
    if (!date) {
      setFieldValue(name, "");
      return;
    }
    if (mode === "year") {
      setFieldValue(name, date.getFullYear().toString());
    } else if (mode === "month-year") {
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      setFieldValue(name, `${year}-${month}`);
    } else {
      // full (store yyyy-MM-dd in UTC to avoid TZ drift)
      const iso = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      )
        .toISOString()
        .split("T")[0];
      setFieldValue(name, iso);
    }
  };

  const showError = meta.touched && meta.error;
  const withPortal = usePortalOnMobile && isMobileLike;

  return (
    <div className={`datepicker-container ${showError ? "has-error" : ""}`}>
      <div
        className={`datepicker-inner-box ${isFocused ? "datepicker-focused" : ""} ${showError ? "has-error" : ""
          }`}
      >
        {label && <div className="datepicker-label">{label}</div>}

        <div className="datepicker-field">
          <DatePickerLib
            selected={selectedDate}
            onChange={handleChange}
            onBlur={(e) => {
              field.onBlur(e);
              setIsFocused(false);
            }}
            onFocus={() => setIsFocused(true)}
            placeholderText={placeholder}
            dateFormat={dateFormat}
            disabled={disabled}
            minDate={finalMinDate}
            maxDate={finalMaxDate}
            className="datepicker-input"
            showMonthDropdown={mode === "full"}
            showYearDropdown
            showMonthYearPicker={showMonthYearPicker}
            showYearPicker={showYearPicker}
            dropdownMode="select"
            id={name}
            // keep calendar under input
            popperPlacement="bottom-start"
            // ⬇️ مهم: روی موبایل نبند روی اسکرول (برای جلوگیری از بسته‌شدن لحظه‌ای)
            closeOnScroll={(evt: Event) => {
              if (withPortal) {
                // On mobile / portal mode → never auto-close on scroll
                return false;
              }
              // Desktop: close only when scroll target is outside the popper
              const target = evt.target as HTMLElement | null;
              return target ? !target.closest(".tl-datepicker-popper") : true;
            }}
            // On mobile: render in portal for better viewport behavior
            withPortal={withPortal}
            // Add a class to control width / layout
            popperClassName={`tl-datepicker-popper${withPortal ? " tl-datepicker-popper--portal" : ""
              }`}
            // optional portal id (only used when withPortal = true)
            portalId="tl-datepicker-portal"
          />
        </div>
      </div>

      {showError && <FieldError>{meta.error}</FieldError>}
    </div>
  );
};

export default DatePicker;
